/**
 * Vivre Control Plane — RBAC Configuration
 * 
 * Roles and permissions based on design spec section 4.3
 */

// Role definitions
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  TENANT_ADMIN: 'tenant_admin',
  BILLING_MANAGER: 'billing_manager',
  AUDITOR: 'auditor',
  READONLY_VIEWER: 'readonly_viewer',
};

// Role display labels
export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.TENANT_ADMIN]: 'Tenant Admin',
  [ROLES.BILLING_MANAGER]: 'Billing Manager',
  [ROLES.AUDITOR]: 'Auditor',
  [ROLES.READONLY_VIEWER]: 'Read-Only Viewer',
};

// Role descriptions
export const ROLE_DESCRIPTIONS = {
  [ROLES.SUPER_ADMIN]: 'Full read/write across all tenants and all modules',
  [ROLES.TENANT_ADMIN]: 'Full control over assigned tenants',
  [ROLES.BILLING_MANAGER]: 'Read/write access to subscription and cost modules only',
  [ROLES.AUDITOR]: 'Read-only across all modules including audit logs',
  [ROLES.READONLY_VIEWER]: 'Dashboard-only access with no write permissions',
};

// Permission keys
export const PERMISSIONS = {
  // Tenant management
  TENANTS_READ: 'tenants.read',
  TENANTS_WRITE: 'tenants.write',

  // Lifecycle
  LIFECYCLE_READ: 'lifecycle.read',
  LIFECYCLE_WRITE: 'lifecycle.write',

  // Configuration
  CONFIG_READ: 'config.read',
  CONFIG_WRITE: 'config.write',

  // Cost & Billing
  COST_READ: 'cost.read',
  COST_WRITE: 'cost.write',

  // Health & Status
  HEALTH_READ: 'health.read',
  HEALTH_WRITE: 'health.write',

  // Analytics
  ANALYTICS_READ: 'analytics.read',

  // Subscriptions
  SUBSCRIPTIONS_READ: 'subscriptions.read',
  SUBSCRIPTIONS_WRITE: 'subscriptions.write',

  // Admin
  ADMIN_READ: 'admin.read',
  ADMIN_WRITE: 'admin.write',

  // Audit
  AUDIT_READ: 'audit.read',
};

// Role → Permissions mapping
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: ['*'], // wildcard = all permissions

  [ROLES.TENANT_ADMIN]: [
    PERMISSIONS.TENANTS_READ,
    PERMISSIONS.TENANTS_WRITE,
    PERMISSIONS.LIFECYCLE_READ,
    PERMISSIONS.LIFECYCLE_WRITE,
    PERMISSIONS.CONFIG_READ,
    PERMISSIONS.CONFIG_WRITE,
    PERMISSIONS.HEALTH_READ,
    PERMISSIONS.ANALYTICS_READ,
  ],

  [ROLES.BILLING_MANAGER]: [
    PERMISSIONS.TENANTS_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.COST_WRITE,
    PERMISSIONS.SUBSCRIPTIONS_READ,
    PERMISSIONS.SUBSCRIPTIONS_WRITE,
  ],

  [ROLES.AUDITOR]: [
    PERMISSIONS.TENANTS_READ,
    PERMISSIONS.LIFECYCLE_READ,
    PERMISSIONS.CONFIG_READ,
    PERMISSIONS.COST_READ,
    PERMISSIONS.HEALTH_READ,
    PERMISSIONS.ANALYTICS_READ,
    PERMISSIONS.SUBSCRIPTIONS_READ,
    PERMISSIONS.AUDIT_READ,
  ],

  [ROLES.READONLY_VIEWER]: [
    PERMISSIONS.TENANTS_READ,
    PERMISSIONS.HEALTH_READ,
    PERMISSIONS.ANALYTICS_READ,
  ],
};

// Route → Required permissions mapping
export const ROUTE_PERMISSIONS = {
  '/home': [], // everyone can see home
  '/tenants': [PERMISSIONS.TENANTS_READ],
  '/lifecycle': [PERMISSIONS.LIFECYCLE_READ],
  '/configuration': [PERMISSIONS.CONFIG_READ],
  '/cost': [PERMISSIONS.COST_READ],
  '/health': [PERMISSIONS.HEALTH_READ],
  '/analytics': [PERMISSIONS.ANALYTICS_READ],
  '/subscriptions': [PERMISSIONS.SUBSCRIPTIONS_READ],
  '/admin': [PERMISSIONS.ADMIN_READ],
};

// Helper: Check if a role has a specific permission
export const hasPermission = (role, permission) => {
  const rolePerms = ROLE_PERMISSIONS[role] || [];
  if (rolePerms.includes('*')) return true;
  return rolePerms.includes(permission);
};

// Helper: Check if a role has ANY of the given permissions
export const hasAnyPermission = (role, permissions = []) => {
  if (!permissions.length) return true; // no permissions required = open
  const rolePerms = ROLE_PERMISSIONS[role] || [];
  if (rolePerms.includes('*')) return true;
  return permissions.some((p) => rolePerms.includes(p));
};

// Helper: Get all routes a role can access
export const getAccessibleRoutes = (role) => {
  return Object.entries(ROUTE_PERMISSIONS)
    .filter(([, perms]) => hasAnyPermission(role, perms))
    .map(([route]) => route);
};
